import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminPostbackInfo } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Copy, Check, ExternalLink, Webhook, AlertTriangle } from 'lucide-react';

const EVENT_LABELS = {
  registration: 'REGISTRATION',
  email_confirmation: 'EMAIL CONFIRMATION',
  ftd: 'FTD (First Time Deposit)',
  redeposit: 'REDEPOSIT',
  withdrawal: 'WITHDRAWAL',
};

const EVENT_DESC = {
  registration: 'Trader has registered.',
  email_confirmation: 'Trader confirmed their email.',
  ftd: 'Trader made their first deposit.',
  redeposit: 'Trader made a repeated deposit.',
  withdrawal: 'Trader initiated a withdrawal.',
};

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={copy}
      className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
      data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {done ? <Check className="h-3.5 w-3.5 text-[hsl(152_78%_55%)]" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function InfoTile({ label, value, hint, mono = false }) {
  return (
    <div className="rounded-md border border-border bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`mt-1 truncate text-xs ${mono ? 'font-mono' : ''}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminPostbackSetup() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('A'); // 'A' = checkbox method (default), 'B' = manual macros

  useEffect(() => {
    (async () => {
      try {
        setInfo(await adminPostbackInfo());
      } catch {
        toast.error('Failed to load postback info');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="card-surface" data-testid="postback-setup-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">Loading postback config…</CardContent>
      </Card>
    );
  }
  if (!info) return null;

  const methodA = info.method_a || {};
  const baseUrls = methodA.base_urls || {};
  const dataFields = methodA.data_format_fields || {};
  const urlsB = info.postback_urls || {};

  return (
    <Card className="card-surface" data-testid="postback-setup">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Webhook className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Pocket Option · Postback Setup
        </CardTitle>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Configure these once in your Pocket Option Partners panel. After setup, registrations and
          deposits will appear automatically in the Broker Analytics tab above.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* CRITICAL WARNING */}
        <div className="rounded-md border border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.08)] p-3 flex gap-2 items-start" data-testid="postback-warning">
          <AlertTriangle className="h-4 w-4 text-[hsl(0_72%_55%)] mt-0.5 flex-shrink-0" />
          <div className="text-xs leading-relaxed text-[hsl(0_0%_88%)]">
            <strong>Already created postbacks in PO panel?</strong> Delete them first and re-create using
            the steps below. Older setups using <code className="font-mono">{'{CLICK_ID}'}</code> (uppercase)
            do NOT work — Pocket Option only replaces lowercase macros.
          </div>
        </div>

        {/* TROUBLESHOOTING — registration not tracking */}
        <div className="rounded-md border border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.06)] p-3 flex gap-2 items-start" data-testid="postback-troubleshoot">
          <AlertTriangle className="h-4 w-4 text-[hsl(43_92%_56%)] mt-0.5 flex-shrink-0" />
          <div className="text-xs leading-relaxed text-[hsl(0_0%_88%)] space-y-1.5">
            <div><strong>New account registration showing as FTD instead of "Registration"?</strong></div>
            <div className="text-muted-foreground">
              Pocket Option fires each event separately — you need a <strong>different postback URL for
              every event you want to track</strong>. Most commonly people only add the FTD URL and forget
              registration. Open <em>Pocket Partners → Postbacks → Add</em> 5 times — once for each event
              shown below (Registration, Email Confirmation, FTD, Redeposit, Withdrawal). If the
              <strong> Registration</strong> postback isn't created in PO panel, you'll never see registrations
              in the Broker Analytics tab — only the events you actually configured will show up.
            </div>
          </div>
        </div>

        {/* Quick info */}
        <div className="grid gap-2 sm:grid-cols-3">
          <InfoTile label="Tracking Param" value={info.tracking_param} hint="Auto-attached to broker link" />
          <InfoTile label="Postback Secret" value={info.secret} hint="Already embedded in URLs" mono />
          <InfoTile label="Redirect URL" value={info.redirect_url} hint="Used by website, not PO" mono />
        </div>

        {/* Method toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Setup Method</span>
          <div className="inline-flex rounded-md border border-white/10 overflow-hidden text-[11px]">
            <button
              type="button"
              onClick={() => setMethod('A')}
              className={`px-3 py-1.5 transition-colors ${method === 'A' ? 'bg-[hsl(43_92%_56%)] text-black font-semibold' : 'bg-white/[0.02] text-muted-foreground hover:text-white'}`}
              data-testid="method-a-btn"
            >
              A · Checkbox (Recommended)
            </button>
            <button
              type="button"
              onClick={() => setMethod('B')}
              className={`px-3 py-1.5 transition-colors ${method === 'B' ? 'bg-[hsl(43_92%_56%)] text-black font-semibold' : 'bg-white/[0.02] text-muted-foreground hover:text-white'}`}
              data-testid="method-b-btn"
            >
              B · Manual macros
            </button>
          </div>
        </div>

        {/* METHOD A */}
        {method === 'A' && (
          <div className="space-y-3" data-testid="method-a-block">
            <div className="rounded-md border border-[rgba(245,185,66,0.25)] bg-[rgba(245,185,66,0.04)] p-3 text-xs leading-relaxed">
              <div className="font-semibold text-[hsl(43_92%_56%)] mb-2">How to set up (Method A)</div>
              <ol className="list-decimal pl-4 space-y-1.5 text-[hsl(0_0%_85%)]">
                <li>In Pocket Partners, open <strong>Postbacks</strong> → click <strong>Add</strong>.</li>
                <li><strong>Campaign:</strong> select your campaign (or All).</li>
                <li><strong>Method:</strong> GET</li>
                <li>
                  <strong>URL field:</strong> paste the matching base URL below for the event you are configuring.
                </li>
                <li>
                  <strong>Data Format section:</strong> tick the boxes for the fields listed under each event.
                  Keep the field display names exactly as they are (e.g. <code className="font-mono">click_id</code>,
                  <code className="font-mono">trader_id</code>, <code className="font-mono">country</code>,
                  <code className="font-mono">sumdep</code>).
                </li>
                <li>Click <strong>Save</strong>. Repeat for each event you want to track.</li>
              </ol>
            </div>

            {Object.entries(baseUrls).map(([event, url]) => (
              <div key={event} className="rounded-md border border-border bg-white/[0.02] overflow-hidden" data-testid={`postback-row-${event}`}>
                <div className="flex items-center justify-between px-3 py-2 bg-white/[0.02] border-b border-border">
                  <div>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)] font-semibold">
                      {EVENT_LABELS[event] || event.toUpperCase()}
                    </span>
                    <span className="ml-2 text-[10px] text-muted-foreground">{EVENT_DESC[event] || ''}</span>
                  </div>
                  <CopyButton text={url} label={event} />
                </div>
                <div className="px-3 py-2 font-mono text-[11px] break-all text-[hsl(0_0%_85%)]">{url}</div>
                {dataFields[event] && (
                  <div className="px-3 py-2 border-t border-border bg-black/20 text-[11px]">
                    <span className="text-muted-foreground mr-1.5">Tick these in Data Format:</span>
                    {dataFields[event].map((f) => (
                      <span
                        key={f}
                        className="inline-block mr-1 mb-1 px-1.5 py-0.5 rounded border border-[hsl(43_92%_56%)]/30 bg-[hsl(43_92%_56%)]/10 text-[hsl(43_92%_56%)] font-mono"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* METHOD B */}
        {method === 'B' && (
          <div className="space-y-3" data-testid="method-b-block">
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-xs leading-relaxed">
              <div className="font-semibold text-white mb-1">Manual macro URLs (lowercase)</div>
              <p className="text-muted-foreground">
                Use this if your PO panel doesn't show a Data Format section. Paste the full URL into the URL
                field — Pocket Option will replace the <code className="font-mono">{'{click_id}'}</code>,
                <code className="font-mono">{'{trader_id}'}</code>, <code className="font-mono">{'{country}'}</code>,
                <code className="font-mono">{'{sumdep}'}</code> macros at runtime.
              </p>
            </div>

            {Object.entries(urlsB).map(([event, url]) => (
              <div key={event} className="rounded-md border border-border bg-white/[0.02] overflow-hidden" data-testid={`postback-row-b-${event}`}>
                <div className="flex items-center justify-between px-3 py-2 bg-white/[0.02] border-b border-border">
                  <div>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)] font-semibold">
                      {EVENT_LABELS[event] || event.toUpperCase()}
                    </span>
                    <span className="ml-2 text-[10px] text-muted-foreground">{EVENT_DESC[event] || ''}</span>
                  </div>
                  <CopyButton text={url} label={event} />
                </div>
                <div className="px-3 py-2 font-mono text-[11px] break-all text-[hsl(0_0%_85%)]">{url}</div>
              </div>
            ))}
          </div>
        )}

        <a
          href="https://po-partners.com/postbacks"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-[hsl(43_92%_56%)] hover:underline"
          data-testid="open-pocket-dashboard"
        >
          Open Pocket Option Partners <ExternalLink className="h-3 w-3" />
        </a>
      </CardContent>
    </Card>
  );
}
